/*
 * Total Maskiner — custom-lag (pt-custom.js)
 * ------------------------------------------------------------------
 * ProTruck-/Total Maskiner-specifik JavaScript oven på vendor-temaet Entreprise.
 * Loades sitewide med defer via snippets/pt-head.liquid (hook `pt-layer` i
 * layout/theme.liquid). Ingen afhængigheder. Offentlige funktioner hænges på
 * window.PT. Hver blok markeres med PT-CUSTOM: <id> og registreres i
 * CUSTOMIZATIONS.md.
 */
(function () {
  'use strict';

  window.PT = window.PT || {};

  /* PT-CUSTOM: product-addon — tilvalg på produktsiden og i quick-add.
   * Markup: snippets/pt-product-addons.liquid → pt-addon-rows.liquid → pt-addon-row.liquid
   * (+ antalsvælgeren snippets/pt-qty.liquid).
   * - addSelected() lægger de afkrydsede tilvalg i kurven med én /cart/add.js-anmodning (hvert
   *   tilvalg med rækkens antal), efter at hovedproduktet er lagt i kurven. Shopify afviser hele
   *   anmodningen, hvis ét tilvalg fejler; så prøves hvert tilvalg for sig. Kaldes fra hooket i
   *   assets/product-form.js. Kaster aldrig; returnerer { data?, error? }.
   * - Antalsvælger ([data-pt-qty], fælles med powerstep'et): +/− og manuel indtastning.
   *   I produktboksen følger rækkens antal hovedproduktets antal, indtil kunden selv ændrer det
   *   (data-touched på input'et).
   * - Delegerede lyttere på document (virker også for quick-add-markup, der injiceres senere):
   *   variantvalg opdaterer pris og afkrydser rækken; klik på checkboxen (eller "Tilføj" i
   *   powerstep'et) uden valgt variant åbner vælgeren i stedet; hovedproduktets antal opdaterer
   *   rækkernes antal.
   * - Foldeknap ([data-pt-addons-toggle], kun synlig på mobil i produktsidens formular — se
   *   pt-custom.css): klik folder listen ud/sammen (.is-open + aria-expanded). Tælleren viser
   *   "N muligheder" eller "N valgt" og opdateres ved afkrydsning, variantvalg, køb og pageshow.
   *   Fejler et tilvalg ved køb, foldes listen ud, så kunden ser, hvad der stadig er afkrydset. */
  const ADDONS = '[data-pt-addons]';
  const ROW = '[data-pt-addon-row]';
  const CHECKBOX = '[data-pt-addon-checkbox]';
  const SELECT = '[data-pt-addon-select]';
  const PRICE = '[data-pt-addon-price]';
  const ADD_BUTTON = '[data-pt-powerstep-add]'; // "Tilføj" i rækker med mode 'button' (powerstep)
  const QTY = '[data-pt-qty]';
  const QTY_INPUT = '[data-pt-qty-input]';
  const QTY_STEP = '[data-pt-qty-step]';
  const PROMPT_CLASS = 'is-prompting'; // rækken beder om en variant (pt-custom.css)
  const PROMPT_DURATION = 1200; // ms
  const TOGGLE = '[data-pt-addons-toggle]'; // foldeknappen (mobil)
  const TOGGLE_COUNT = '[data-pt-addons-count]'; // foldeknappens tæller
  const OPEN_CLASS = 'is-open'; // tilvalgslisten er foldet ud (pt-custom.css)

  // Offentligt API: window.PT.productAddons (addSelected tilføjes nedenfor).
  // lastResult = { mainItem, added: [{ variantId, quantity }], failed: [{ title, variantId }], quantity }
  // (quantity = hovedproduktets antal).
  const api = { lastResult: null };

  /**
   * Fortolker et antal; ugyldige værdier og værdier under 1 giver 1.
   * @param {string} value - Rå værdi fra et input.
   * @returns {number}
   */
  const parseQuantity = (value) => {
    const qty = parseInt(value, 10);
    return Number.isFinite(qty) && qty > 1 ? qty : 1;
  };

  /**
   * Normaliserer en antalsvælgers værdi til et helt tal inden for min/max (ugyldig/under min → min)
   * og skriver den tilbage i feltet, hvis den afviger.
   * @param {HTMLInputElement} input - [data-pt-qty-input].
   * @returns {number} Den normaliserede værdi.
   */
  const normalizeQty = (input) => {
    const min = Math.max(parseInt(input.min, 10) || 1, 1);
    const max = parseInt(input.max, 10);
    let qty = parseInt(input.value, 10);
    if (!Number.isFinite(qty) || qty < min) qty = min;
    if (Number.isFinite(max) && max >= min && qty > max) qty = max;
    if (input.value !== String(qty)) input.value = String(qty);
    return qty;
  };

  /**
   * +/− i en antalsvælger: stepUp/stepDown inden for min/max; bubbling `change` kun ved ændring.
   * @param {HTMLButtonElement} button - [data-pt-qty-step].
   */
  const handleQtyStep = (button) => {
    const wrapper = button.closest(QTY);
    const input = wrapper ? wrapper.querySelector(QTY_INPUT) : null;
    if (!input || input.disabled || button.disabled) return;

    const before = input.value;
    normalizeQty(input);
    try {
      if (Number(button.dataset.ptQtyStep) < 0) input.stepDown();
      else input.stepUp();
    } catch (error) {
      // stepUp/stepDown kaster kun ved ugyldig step-konfiguration; værdien normaliseres nedenfor.
    }
    normalizeQty(input);

    if (input.value !== before) input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Hovedproduktets antal i formularen.
   * @param {HTMLFormElement} form - Produktformularen.
   * @returns {number}
   */
  const getQuantity = (form) => {
    const input = form.querySelector('[name="quantity"]');
    return input ? parseQuantity(input.value) : 1;
  };

  /**
   * En tilvalgsrækkes antal: følger hovedproduktets antal, indtil kunden selv har ændret det.
   * Skriver hovedantallet i rækkens felt, når det ikke er ændret af kunden.
   * @param {Element} row - [data-pt-addon-row] i produktformularen.
   * @param {number} mainQuantity - Hovedproduktets antal.
   * @returns {number}
   */
  const syncRowQuantity = (row, mainQuantity) => {
    const input = row ? row.querySelector(QTY_INPUT) : null;
    if (!input) return mainQuantity;
    if (input.dataset.touched === 'true') return normalizeQty(input);
    input.value = String(mainQuantity);
    return mainQuantity;
  };

  /**
   * Hovedproduktets antal ændret (eller genskabt): opdatér de rækker, kunden ikke selv har ændret.
   * @param {HTMLFormElement} form - Produktformularen.
   * @param {number} quantity - Hovedproduktets antal.
   */
  const syncRowQuantities = (form, quantity) => {
    form.querySelectorAll(`${ADDONS} ${ROW}`).forEach((row) => syncRowQuantity(row, quantity));
  };

  /**
   * De afkrydsede tilvalg i formularen med variant-id og antal.
   * @param {HTMLFormElement} form - Produktformularen.
   * @returns {Array<{row: Element, checkbox: HTMLInputElement, variantId: number, quantity: number, title: string}>}
   */
  const getSelected = (form) => {
    const selected = [];
    const mainQuantity = getQuantity(form);
    form.querySelectorAll(`${ADDONS} ${CHECKBOX}:checked:not(:disabled)`).forEach((checkbox) => {
      const row = checkbox.closest(ROW);
      if (!row) return;

      let variantId = checkbox.dataset.ptAddonVariantId;
      if (!variantId) {
        const select = row.querySelector(SELECT);
        variantId = select ? select.value : '';
      }

      const id = parseInt(variantId, 10);
      if (!id) return;

      selected.push({
        row,
        checkbox,
        variantId: id,
        quantity: syncRowQuantity(row, mainQuantity),
        title: row.dataset.ptAddonTitle || ''
      });
    });
    return selected;
  };

  /**
   * Erstatter en pladsholder i en oversat tekst. Tolererer {{ key }}, {{key}} og [key].
   * @param {string} template - Tekst med pladsholdere.
   * @param {string} key - Pladsholderens navn.
   * @param {string} value - Værdi, der indsættes.
   * @returns {string}
   */
  const fillPlaceholder = (template, key, value) => template.replace(
    new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}|\\[${key}\\]`, 'g'),
    () => value
  );

  /**
   * Bygger fejlteksten fra fieldset'ets data-error-template.
   * @param {HTMLFormElement} form - Produktformularen.
   * @param {Array<{title: string}>} failed - Tilvalg, der ikke kom i kurven.
   * @param {string} reason - Årsag fra Shopify (kan være tom).
   * @returns {string}
   */
  const buildError = (form, failed, reason) => {
    const fieldset = form.querySelector(ADDONS);
    const template = (fieldset && fieldset.dataset.errorTemplate) || '[addons]. [reason]';
    const addons = failed.map((item) => item.title).filter(Boolean).join(', ');
    let message = fillPlaceholder(template, 'addons', addons);
    message = fillPlaceholder(message, 'reason', reason || '');
    return message.replace(/\s+/g, ' ').trim();
  };

  /**
   * Folder tilvalgslisten ud eller sammen (.is-open på fieldset'et + foldeknappens aria-expanded).
   * Har kun synlig effekt på mobil i produktsidens formular; ellers er listen altid vist.
   * @param {Element|null} fieldset - [data-pt-addons].
   * @param {boolean} open - true = foldet ud.
   */
  const setAddonsOpen = (fieldset, open) => {
    if (!fieldset) return;
    fieldset.classList.toggle(OPEN_CLASS, open);
    const toggle = fieldset.querySelector(TOGGLE);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  /**
   * Foldeknappens tæller: "N valgt", når der er afkrydsede tilvalg (.has-chosen), ellers
   * "N muligheder". Teksterne ligger på knappen (data-chosen-template / data-options-text).
   * No-op uden foldeknap.
   * @param {Element|null} fieldset - [data-pt-addons].
   */
  const updateAddonsSummary = (fieldset) => {
    const toggle = fieldset ? fieldset.querySelector(TOGGLE) : null;
    const count = toggle ? toggle.querySelector(TOGGLE_COUNT) : null;
    if (!count) return;

    const chosen = fieldset.querySelectorAll(`${CHECKBOX}:checked:not(:disabled)`).length;
    count.textContent = chosen > 0
      ? fillPlaceholder(toggle.dataset.chosenTemplate || '[number]', 'number', String(chosen))
      : (toggle.dataset.optionsText || '');
    toggle.classList.toggle('has-chosen', chosen > 0);
  };

  /**
   * Generel fejltekst fra temaet (bruges, når Shopify ikke giver en årsag).
   * @returns {string}
   */
  const genericError = () => (window.theme && window.theme.strings && window.theme.strings.cartError) || '';

  /**
   * Årsagen i et fejlsvar fra Shopifys Cart API.
   * @param {object|null} json - Svaret (kan være null).
   * @returns {string}
   */
  const errorReason = (json) => {
    if (!json) return '';
    return (typeof json.description === 'string' ? json.description : json.message) || '';
  };

  /**
   * POST til Shopifys Cart API (JSON).
   * @param {string} route - Fx theme.routes.cartAdd (uden .js).
   * @param {object} body - Anmodningens JSON.
   * @returns {Promise<{ok: boolean, json: object|null}>}
   */
  const postCart = async (route, body) => {
    const response = await fetch(`${route}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });

    let json = null;
    try {
      json = await response.json();
    } catch (parseError) {
      json = null;
    }
    return { ok: response.ok && !!json, json };
  };

  /**
   * Lægger tilvalg i kurven med én /cart/add.js-anmodning.
   * @param {Array<{variantId: number, quantity: number}>} items - Tilvalgene med antal.
   * @param {string|string[]} [sections] - Sektioner til Section Rendering API.
   * @returns {Promise<{ok: boolean, json: object|null, ids: Set<number>}>} ids = variant-id'er i svaret.
   */
  const postItems = async (items, sections) => {
    const route = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add';
    const { ok, json } = await postCart(route, {
      items: items.map((item) => ({ id: item.variantId, quantity: parseQuantity(item.quantity) })),
      sections,
      sections_url: window.location.pathname
    });

    const ids = new Set(
      (ok && Array.isArray(json.items) ? json.items : []).map((item) => Number(item.variant_id))
    );
    return { ok, json, ids };
  };

  /**
   * Lægger formularens afkrydsede tilvalg i kurven med én anmodning.
   * @param {HTMLFormElement} form - Produktformularen (den, hovedproduktet blev sendt fra).
   * @param {object} mainItem - Svaret fra hovedproduktets /cart/add.
   * @param {string|string[]} [sections] - Sektioner til Section Rendering API (videresendes).
   * @returns {Promise<{data?: object, error?: string}>} data = /cart/add.js-svaret (med sections),
   *   error = fejltekst til kunden. Kaster aldrig.
   */
  const addSelected = async (form, mainItem, sections) => {
    let selected = [];
    let quantity = 1;

    try {
      if (!form || !form.querySelector(ADDONS)) {
        api.lastResult = { mainItem, added: [], failed: [], quantity };
        return {};
      }

      quantity = getQuantity(form);
      selected = getSelected(form);

      if (!selected.length) {
        api.lastResult = { mainItem, added: [], failed: [], quantity };
        return {};
      }

      const added = [];
      const failed = [];
      let data = null;
      let reason = '';

      const markAdded = (item) => {
        added.push({ variantId: item.variantId, quantity: item.quantity });
        // Kom i kurven: fjern afkrydsningen (en valgt variant i select beholdes), og lad rækkens
        // antal følge hovedproduktets antal igen.
        item.checkbox.checked = false;
        const input = item.row.querySelector(QTY_INPUT);
        if (input) delete input.dataset.touched;
      };
      const markFailed = (item) => failed.push({ title: item.title, variantId: item.variantId });

      const batch = await postItems(selected, sections);
      if (batch.ok) {
        data = batch.json;
        selected.forEach((item) => (batch.ids.has(item.variantId) ? markAdded(item) : markFailed(item)));
      } else if (selected.length > 1) {
        // Shopify afviser hele anmodningen, hvis ét tilvalg fejler (fx udsolgt) —
        // prøv derfor hvert tilvalg for sig, så de øvrige stadig kommer i kurven.
        for (const item of selected) {
          const single = await postItems([item], sections); // eslint-disable-line no-await-in-loop
          if (single.ok && single.ids.has(item.variantId)) {
            data = single.json;
            markAdded(item);
          } else {
            markFailed(item);
            if (!reason) reason = errorReason(single.json);
          }
        }
      } else {
        reason = errorReason(batch.json);
        selected.forEach(markFailed);
      }

      syncRowQuantities(form, quantity);
      // Tilføjede rækker er afkrydset fra: opdatér foldeknappens tæller. Fejlede tilvalg er stadig
      // afkrydset — fold listen ud, så kunden kan se dem.
      const fieldset = form.querySelector(ADDONS);
      updateAddonsSummary(fieldset);
      if (failed.length) setAddonsOpen(fieldset, true);
      api.lastResult = { mainItem, added, failed, quantity };

      const result = {};
      if (data && data.sections) result.data = data;
      if (failed.length) result.error = buildError(form, failed, reason || (added.length ? '' : genericError()));
      return result;
    } catch (error) {
      try {
        const failed = selected.map((item) => ({ title: item.title, variantId: item.variantId }));
        api.lastResult = { mainItem, added: [], failed, quantity };
        if (failed.length) {
          const fieldset = form.querySelector(ADDONS);
          updateAddonsSummary(fieldset);
          setAddonsOpen(fieldset, true);
        }
        return failed.length ? { error: buildError(form, failed, genericError()) } : {};
      } catch (innerError) {
        return {};
      }
    }
  };

  /**
   * Rækken har en variantvælger, hvor der endnu ikke er valgt en variant.
   * @param {Element|null} row - [data-pt-addon-row].
   * @returns {boolean}
   */
  const needsVariant = (row) => {
    const select = row ? row.querySelector(SELECT) : null;
    return !!select && select.value === '';
  };

  const promptTimers = new WeakMap(); // række → timer, der fjerner fremhævningen

  /**
   * Klik på checkbox/"Tilføj" uden valgt variant: fokus til vælgeren, som åbnes, hvor browseren
   * understøtter showPicker(), og "Vælg variant" fremhæves kort (.is-prompting).
   * @param {Element} row - [data-pt-addon-row].
   */
  const promptVariant = (row) => {
    const select = row.querySelector(SELECT);
    if (!select || select.disabled) return;

    select.focus({ preventScroll: true });
    if (typeof select.showPicker === 'function') {
      try {
        select.showPicker();
      } catch (error) {
        // Ikke understøttet her (fx ældre Safari): fokus + fremhævning står alene.
      }
    }

    row.classList.remove(PROMPT_CLASS);
    row.getBoundingClientRect(); // reflow → animationen starter forfra ved gentagne klik
    row.classList.add(PROMPT_CLASS);
    window.clearTimeout(promptTimers.get(row));
    promptTimers.set(row, window.setTimeout(() => row.classList.remove(PROMPT_CLASS), PROMPT_DURATION));
  };

  /**
   * Variantvalg i en række: opdatér prisen og vælgerens linje; i mode 'checkbox' afkrydses rækken
   * (fravalg af variant fjerner afkrydsningen).
   * @param {HTMLSelectElement} select - Rækkens variant-select.
   */
  const handleVariantChange = (select) => {
    const row = select.closest(ROW);
    if (!row) return;

    const hasValue = select.value !== '';
    row.classList.remove(PROMPT_CLASS);
    const option = select.options[select.selectedIndex];
    const price = row.querySelector(PRICE);
    if (price) {
      const text = hasValue && option ? option.dataset.price : price.dataset.ptAddonFrom;
      if (text) price.textContent = text;
    }
    row.classList.toggle('is-chosen', hasValue); // skjuler "fra" (pt-custom.css)

    // Tekst-vælgerens synlige linje: "Variant: <titel>" eller "Vælg variant".
    const value = row.querySelector('[data-pt-addon-select-value]');
    if (value) {
      value.textContent = hasValue && option
        ? (option.dataset.title || option.textContent.trim())
        : (value.dataset.placeholder || '');
    }
    const prefix = row.querySelector('[data-pt-addon-select-prefix]');
    if (prefix) prefix.hidden = !hasValue;

    const checkbox = row.querySelector(CHECKBOX);
    if (checkbox) {
      checkbox.checked = hasValue;
      updateAddonsSummary(row.closest(ADDONS));
      const form = checkbox.form || row.closest('form');
      if (hasValue && form) syncRowQuantity(row, getQuantity(form));
    }
  };

  document.addEventListener('click', (evt) => {
    const { target } = evt;
    if (!(target instanceof Element)) return;

    // Afkrydsning (også via rækkens labels) uden valgt variant: fortryd den, og bed om en variant.
    if (target.matches(CHECKBOX)) {
      const row = target.closest(ROW);
      if (target.checked && needsVariant(row)) {
        evt.preventDefault();
        promptVariant(row);
      }
      return;
    }

    // Foldeknappen (mobil): fold tilvalgslisten ud/sammen. Fokus bliver på knappen.
    const toggle = target.closest(TOGGLE);
    if (toggle) {
      const fieldset = toggle.closest(ADDONS);
      setAddonsOpen(fieldset, !(fieldset && fieldset.classList.contains(OPEN_CLASS)));
      return;
    }

    const button = target.closest(QTY_STEP);
    if (!button) return;
    evt.preventDefault();
    handleQtyStep(button);
  });

  // Enter i et antalsfelt må ikke sende produktformularen; blur/focus bekræfter værdien (change).
  document.addEventListener('keydown', (evt) => {
    if (evt.key !== 'Enter') return;
    const { target } = evt;
    if (!(target instanceof HTMLInputElement) || !target.matches(QTY_INPUT)) return;
    evt.preventDefault();
    target.blur();
    target.focus();
  });

  document.addEventListener('change', (evt) => {
    const { target } = evt;
    if (!(target instanceof Element)) return;

    if (target.matches(QTY_INPUT)) {
      normalizeQty(target);
      // Kunden har selv ændret rækkens antal: det følger ikke længere hovedproduktets antal.
      if (target.closest(ROW)) target.dataset.touched = 'true';
      return;
    }

    if (target.matches(SELECT)) {
      handleVariantChange(target);
      return;
    }

    if (target.matches(CHECKBOX)) {
      const form = target.form || target.closest('form');
      if (target.checked && form) syncRowQuantity(target.closest(ROW), getQuantity(form));
      updateAddonsSummary(target.closest(ADDONS));
      return;
    }

    if (target.matches('[name="quantity"]')) {
      const form = target.form || target.closest('form');
      if (!form || !form.querySelector(ADDONS)) return;
      syncRowQuantities(form, parseQuantity(target.value));
    }
  });

  // Tilstand, som browseren har genskabt (fx ved "tilbage"): synkronisér rækkernes antal med
  // hovedproduktets antal, og lad foldeknappens tæller følge de genskabte afkrydsninger.
  const syncAllAddons = () => {
    document.querySelectorAll(ADDONS).forEach((fieldset) => {
      const form = fieldset.closest('form');
      if (form) syncRowQuantities(form, getQuantity(form));
      updateAddonsSummary(fieldset);
    });
  };

  syncAllAddons();
  window.addEventListener('pageshow', syncAllAddons);

  api.addSelected = addSelected;
  window.PT.productAddons = api;
  /* PT-CUSTOM: product-addon — slut */

  /* PT-CUSTOM: powerstep — bekræftelsesdialog efter "Læg i bestilling".
   * Markup: sections/pt-powerstep.liquid (hentes med Section Rendering API på produktets URL,
   * så `product` og `cart` er i kontekst) + snippets/pt-addon-row.liquid (mode 'button').
   * - Tænd/sluk: kun aktiv, når temaindstillingen after_add_to_cart = "nothing"
   *   (theme.settings.afterAtc). Lytter på on:cart:add / on:line-item:change fra <product-form>
   *   (assets/product-form.js); springer quick-add-skuffen (egen bekræftelse) og kurvsiden over.
   * - Viser de netop tilføjede linjer (hovedvare + PT.productAddons.lastResult.added) med
   *   antalsvælger, fragtbar, tilvalg der fejlede, antal/total og op til 4 tilvalg med antal +
   *   "Tilføj" i ét klik (dialogen forbliver åben).
   * - Linjernes antal ændres via /cart/change.js (debounce, én anmodning ad gangen, seneste værdi
   *   pr. linje vinder); derefter gen-renderes dialogen. Fejl vises på linjen, og værdien går tilbage.
   * - Med anbefalinger (kortet har .pt-powerstep__card--split) får <dialog> .pt-powerstep--split
   *   (≥900px: linjer og anbefalinger side om side).
   * - Fallback: kan sektionen ikke hentes/vises, sendes kunden til kurven, så der altid er en
   *   bekræftelse. Kaster aldrig ud af event-lytterne.
   * - PT-CUSTOM: cart-analytics — klik på kortets link til kurven sender powerstep_to_cart (ptTrack). */
  const PS_SECTION = 'pt-powerstep';
  const PS_ROOT = '[data-pt-powerstep]';
  const PS_HEADING = '[data-pt-powerstep-heading]';
  const PS_LINE = '[data-pt-line]';
  const PS_LINE_REMOVE = '[data-pt-line-remove]'; // skraldespand på en kurvlinje (antal 0)
  const PS_WARNING = '[data-pt-powerstep-warning]';
  const PS_CLOSE = '[data-pt-powerstep-close]';
  const PS_OPEN_CLASS = 'pt-powerstep-open'; // på <html>: låser sidescroll, mens dialogen er åben
  const PS_SPLIT_CARD_CLASS = 'pt-powerstep__card--split';
  const PS_SPLIT_CLASS = 'pt-powerstep--split'; // på <dialog>: bredere kort ≥900px
  const PS_LINE_DEBOUNCE = 300; // ms efter sidste ændring af et linje-antal

  let psDialog = null;
  let psContent = null;
  let psStatus = null;
  let psState = null; // { url, variantIds: number[], failed: [{ title, variantId }], quantity }
  let psConsumed = null; // senest brugte lastResult (et gammelt resultat genbruges ikke)
  let psToken = 0; // øges ved hver åbning og lukning; forældede svar ignoreres
  let psReturnFocus = null;
  let psCleanedUp = true; // true, når dialogen er lukket og oprydningen (scroll-lås, fokus) er sket
  let psPointerOnBackdrop = false;
  const psLineChanges = new Map(); // linje-key → ønsket antal (seneste vinder)
  let psLineTimer = 0;
  let psLineBusy = false; // en /cart/change.js-runde er i gang
  let psAddBusy = 0; // antal igangværende "Tilføj"-anmodninger

  const cartUrl = () => (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  const goToCart = () => {
    window.location.href = cartUrl();
  };
  const powerstepEnabled = () => !!(
    window.theme && window.theme.settings && window.theme.settings.afterAtc === 'nothing'
  );
  const dialogSupported = () => typeof window.HTMLDialogElement === 'function'
    && typeof window.HTMLDialogElement.prototype.showModal === 'function';

  /* PT-CUSTOM: cart-analytics — lille kopi af ptTrack fra assets/pt-cart.js (K2), samme regler: kun
   * whitelistede nøgler med værdier fra faste sæt; Shopify.analytics.publish('pt:<navn>'); GA4 kun via
   * appens window.gtag med analytics-samtykke (Shopify.customerPrivacy.analyticsProcessingAllowed, når den
   * findes) og send_to G-5B8C4PJHTM; ?pt_debug=1/0 (sessionStorage pt_debug) → console.info, debug_mode
   * og én gang pr. side gtag('set', { debug_mode: true }). Kaster aldrig og forhindrer intet.
   * PT-CUSTOM: pt-notes — ptTrack er også window.PT.track (sitewide; bruges af assets/pt-notes.js), og en
   * skema-værdi kan være en RegExp (fx notens tracking_name) i stedet for et fast sæt. */
  const PT_GA4_ID = 'G-5B8C4PJHTM';
  const PT_NOTE_NAME = /^[a-z0-9-]{1,40}$/;
  const PT_NOTE_DESIGNS = ['expert', 'light', 'dark', 'compact'];
  // PT-CUSTOM: maskinvaelger-open — vælgerens indgange (D4); samme liste som UI.SOURCES i pt-selector.js. Loaderen
  // (SEL_SOURCES) accepterer kun disse som ?kilde=; 'direct' (T1: direkte besøg på siden) findes kun i hændelserne.
  const PT_SEL_SOURCES = ['pdp', 'pdp_soldout', 'grid', 'note', 'link', 'page'];
  // PT-CUSTOM: maskinvaelger-page — måling af resultat og valg (T1). Handles og svar-keys, aldrig fritekst.
  const PT_SEL_EVENT_SOURCES = PT_SEL_SOURCES.concat('direct');
  const PT_SEL_MODES = ['dialog', 'page'];
  const PT_SEL_OUTCOMES = ['match', 'nomatch'];
  const PT_SEL_HANDLE = /^[a-z0-9-]{1,100}$/; // handle eller 'none'
  const PT_SEL_KEYS = /^(?=.{1,100}$)[a-z0-9_-]+(,[a-z0-9_-]+)*$/; // kommaseparerede svar-/spørgsmålskeys eller 'none'
  const PT_EVENTS = {
    powerstep_to_cart: { source: ['cart_link'] },
    // PT-CUSTOM: contact-analytics — tel:/mailto:-klik på alle sider og bekræftet indsendte kontaktformularer.
    contact_click: {
      method: ['tel', 'mail'],
      source: ['pdp', 'cart', 'header', 'footer', 'page', 'selector', 'note', 'other']
    },
    contact_form_sent: { source: ['contact', 'selector', 'signup'] },
    note_view: { note: PT_NOTE_NAME, design: PT_NOTE_DESIGNS },
    note_open: { note: PT_NOTE_NAME, design: PT_NOTE_DESIGNS, trigger: ['auto', 'click'] },
    note_click: { note: PT_NOTE_NAME, design: PT_NOTE_DESIGNS, action: ['link', 'selector', 'copy', 'tel'] },
    note_dismiss: { note: PT_NOTE_NAME, design: PT_NOTE_DESIGNS },
    // PT-CUSTOM: maskinvaelger-open — vælgeren åbnet fra en indgang (D4) eller direkte på siden (T1: 'direct').
    selector_open: { source: PT_SEL_EVENT_SOURCES, mode: PT_SEL_MODES },
    // PT-CUSTOM: maskinvaelger-page — resultatet vist (én gang pr. svarsæt) og kundens valg fra resultatet (T1).
    selector_result: {
      path: ['pallet', 'stack', 'weigh', 'any'],
      outcome: PT_SEL_OUTCOMES,
      top: PT_SEL_HANDLE,
      unmet: PT_SEL_KEYS,
      gap: PT_SEL_KEYS,
      answers: /^[a-z0-9_:|,-]{1,100}$/,
      mode: PT_SEL_MODES,
      source: PT_SEL_EVENT_SOURCES
    },
    selector_choice: {
      role: ['top', 'cheaper', 'stronger', 'next', 'nearest', 'none'],
      action: ['product', 'add', 'contact'],
      product: PT_SEL_HANDLE,
      outcome: PT_SEL_OUTCOMES,
      mode: PT_SEL_MODES
    }
  };
  const ptDebug = (() => {
    let flag = null;
    try {
      flag = new URLSearchParams(window.location.search).get('pt_debug');
    } catch (error) {
      // Ingen URL-parametre → ingen debug.
    }
    try {
      if (flag === '1') window.sessionStorage.setItem('pt_debug', '1');
      else if (flag === '0') window.sessionStorage.removeItem('pt_debug');
      else return window.sessionStorage.getItem('pt_debug') === '1';
    } catch (error) {
      // Uden storage gælder kun URL'en.
    }
    return flag === '1';
  })();

  /**
   * Sender én hændelse til GA4 via appens gtag, hvis den findes og der er analytics-samtykke.
   * @param {string} name - Eventnavn fra PT_EVENTS.
   * @param {object} payload - Allerede whitelistede parametre.
   * @returns {string} 'sent', 'skipped: no-gtag', 'skipped: no-consent' eller 'skipped: error'.
   */
  const ptSendGa = (name, payload) => {
    try {
      if (typeof window.gtag !== 'function') return 'skipped: no-gtag';
      let consent = false;
      try {
        const privacy = window.Shopify && window.Shopify.customerPrivacy;
        consent = !privacy || typeof privacy.analyticsProcessingAllowed !== 'function'
          || privacy.analyticsProcessingAllowed() === true;
      } catch (error) {
        consent = false;
      }
      if (!consent) return 'skipped: no-consent';
      if (ptDebug && !window.PT.analyticsDebugSet) {
        window.PT.analyticsDebugSet = true; // delt med pt-cart.js
        window.gtag('set', { debug_mode: true });
      }
      window.gtag('event', name, {
        ...payload,
        send_to: PT_GA4_ID,
        ...(ptDebug ? { debug_mode: true } : {})
      });
      return 'sent';
    } catch (error) {
      return 'skipped: error';
    }
  };

  // PT-CUSTOM: maskinvaelger-open — M6 F2: efter load-hændelsen ét forsøg mere, og findes gtag stadig ikke, ét
  // sidste forsøg PT_GA_RETRY_MS senere. Højst én GA4-afsendelse pr. hændelse; samtykket tjekkes ved forsøget.
  const PT_GA_RETRY_MS = 2000;
  const ptDeferGa = (name, payload) => {
    const log = (ga) => {
      if (ptDebug) console.info('[pt-analytics]', name, payload, ga); // eslint-disable-line no-console
    };
    try {
      window.addEventListener('load', () => {
        try {
          if (typeof window.gtag === 'function') {
            log(`${ptSendGa(name, payload)} (after load)`);
            return;
          }
          window.setTimeout(() => {
            try {
              log(`${ptSendGa(name, payload)} (after load +2s)`);
            } catch (error) {
              // Hændelser må aldrig forstyrre siden.
            }
          }, PT_GA_RETRY_MS);
        } catch (error) {
          // Hændelser må aldrig forstyrre siden.
        }
      }, { once: true });
      return 'deferred: no-gtag';
    } catch (error) {
      return 'skipped: no-gtag';
    }
  };

  /**
   * Sender en hændelse til Shopify og (med samtykke) GA4.
   * PT-CUSTOM: maskinvaelger-open — M6 F2: ved kold indlæsning definerer Google-appen (web pixel) først gtag
   * senere, så hændelser fra sidens indlæsning (selector_open mode page, note_view) blev 'skipped: no-gtag'. Mangler
   * gtag før document.readyState er 'complete', venter GA4-delen på load (ptDeferGa); Shopify sendes straks som før.
   * @param {string} name - Eventnavn fra PT_EVENTS.
   * @param {object} data - Parametre; kun whitelistede nøgler og værdier sendes.
   */
  const ptTrack = (name, data) => {
    try {
      if (!Object.prototype.hasOwnProperty.call(PT_EVENTS, name)) return;
      const schema = PT_EVENTS[name];
      const payload = {};
      Object.keys(schema).forEach((key) => {
        const value = data ? data[key] : undefined;
        if (typeof value !== 'string') return;
        const allowed = schema[key] instanceof RegExp ? schema[key].test(value) : schema[key].includes(value);
        if (allowed) payload[key] = value;
      });

      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        try {
          const published = analytics.publish(`pt:${name}`, { ...payload });
          if (published && typeof published.catch === 'function') published.catch(() => {});
        } catch (error) {
          // Shopify må ikke stoppe GA4.
        }
      }

      let ga = ptSendGa(name, payload);
      if (ga === 'skipped: no-gtag' && document.readyState !== 'complete') ga = ptDeferGa(name, payload);

      if (ptDebug) console.info('[pt-analytics]', name, payload, ga); // eslint-disable-line no-console
    } catch (error) {
      // Hændelser må aldrig forstyrre powerstep'et.
    }
  };
  window.PT.track = ptTrack; // PT-CUSTOM: pt-notes — offentlig på alle sider (noterne sender via den)

  /**
   * Om et link i kortet går til kurven (samme origin og sti som theme.routes.cart).
   * @param {HTMLAnchorElement} link - Linket.
   * @returns {boolean}
   */
  const isCartLink = (link) => {
    try {
      const url = new URL(link.href, window.location.href);
      const trim = (path) => path.replace(/\/+$/, '');
      return url.origin === window.location.origin && trim(url.pathname) === trim(cartUrl());
    } catch (error) {
      return false;
    }
  };

  /**
   * Henter powerstep-sektionen for en produkt-URL og returnerer kortet.
   * @param {string} url - Produkt-URL (fx /products/<handle>?variant=<id>).
   * @returns {Promise<Element>} [data-pt-powerstep] fra svaret. Kaster ved fejl.
   */
  const fetchPowerstep = async (url) => {
    const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${PS_SECTION}`, {
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`powerstep: HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = doc.querySelector(PS_ROOT);
    if (!root) throw new Error('powerstep: section not found');
    return root;
  };

  /**
   * Produkt-URL for en kurvlinje (bruges, når lastResult ikke passer til eventet).
   * @param {number} variantId - Variant-id fra eventet.
   * @returns {Promise<string>} Tom streng, hvis linjen ikke findes.
   */
  const lineUrlFromCart = async (variantId) => {
    const response = await fetch(`${cartUrl()}.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return '';
    const cart = await response.json();
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const line = items.find((item) => Number(item.variant_id) === variantId);
    return line && line.url ? line.url : '';
  };

  /**
   * Opdaterer kurvikonet i headeren med sections-svaret fra /cart/add.js.
   * @param {object|null} json - Svaret.
   */
  const updateCartIcon = (json) => {
    try {
      const ProductForm = window.customElements && window.customElements.get('product-form');
      if (json && json.sections && ProductForm && typeof ProductForm.updateCartIcon === 'function') {
        ProductForm.updateCartIcon(json);
      }
    } catch (error) {
      // Kurvikonet er kosmetisk; dialogen viser den korrekte status.
    }
  };

  const closePowerstep = () => {
    if (psDialog && psDialog.open) psDialog.close();
    // Ryd op med det samme: dialogens close-event kan blive forsinket (fx i en skjult fane), og så
    // ville sidescroll-låsen hænge. handleDialogClose er idempotent, så eventet senere er ufarligt.
    handleDialogClose();
  };

  /**
   * Viser en fejl i en anbefalingsrække (erstatter en tidligere fejl).
   * @param {Element} row - [data-pt-addon-row].
   * @param {string} [message] - Fejltekst; tom = fjern fejlen.
   */
  const setRowError = (row, message) => {
    if (!row) return;
    const old = row.querySelector('.pt-addon__error');
    if (old) old.remove();
    if (!message) return;
    const error = document.createElement('p');
    error.className = 'pt-addon__error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    row.appendChild(error);
  };

  /**
   * Slår en antalsvælgers knapper og felt til/fra.
   * @param {Element|null} scope - Element, der indeholder [data-pt-qty].
   * @param {boolean} disabled - Deaktivér?
   */
  const setQtyDisabled = (scope, disabled) => {
    if (!scope) return;
    scope.querySelectorAll(`${QTY} button, ${QTY_INPUT}`).forEach((control) => {
      control.disabled = disabled; // eslint-disable-line no-param-reassign
    });
  };

  /**
   * "Tilføj"-knappens optaget-tilstand. aria-disabled (ikke disabled), så fokus bliver på knappen.
   * Rækkens variant-select og antalsvælger er disabled imens.
   * @param {HTMLButtonElement} button - Knappen.
   * @param {HTMLSelectElement|null} select - Rækkens variant-select.
   * @param {boolean} busy - Optaget?
   */
  const setAddBusy = (button, select, busy) => {
    button.classList.toggle('is-loading', busy);
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
    if (select) select.disabled = busy;
    setQtyDisabled(button.closest(ROW), busy);
  };

  /**
   * Anvender dialogens tilstand på et nyt kort: viste linjer, advarsel og anbefalingernes antal.
   * @param {Element} card - [data-pt-powerstep].
   */
  const applyPowerstepState = (card) => {
    const visible = new Set(psState.variantIds);
    card.querySelectorAll(PS_LINE).forEach((line) => {
      line.hidden = !visible.has(Number(line.dataset.variantId));
    });

    // Rækkefølge som psState.variantIds (hovedvaren først); kurven lister nyeste først.
    psState.variantIds.forEach((variantId) => {
      const line = card.querySelector(`${PS_LINE}[data-variant-id="${variantId}"]`);
      if (line && line.parentElement) line.parentElement.appendChild(line);
    });

    const warning = card.querySelector(PS_WARNING);
    const titles = psState.failed.map((item) => item.title).filter(Boolean).join(', ');
    if (warning && titles) {
      warning.textContent = fillPlaceholder(warning.dataset.template || '[addons]', 'addons', titles);
      warning.hidden = false;
    }

    // Anbefalingernes antal starter på hovedvarens antal (et nyt kort er aldrig ændret af kunden).
    card.querySelectorAll(`${ROW} ${QTY_INPUT}`).forEach((input) => {
      if (input.dataset.touched !== 'true') input.value = String(psState.quantity); // eslint-disable-line no-param-reassign
    });
  };

  /**
   * Lægger kortet ind i dialogen og anvender tilstanden.
   * @param {Element} root - [data-pt-powerstep] fra et sektionssvar.
   * @returns {Element|null} Overskriften (til fokus).
   */
  const renderPowerstep = (root) => {
    const card = document.importNode(root, true);
    applyPowerstepState(card);
    psContent.replaceChildren(card);
    psDialog.classList.toggle(PS_SPLIT_CLASS, card.classList.contains(PS_SPLIT_CARD_CLASS));

    const heading = card.querySelector(PS_HEADING);
    if (heading && heading.id) psDialog.setAttribute('aria-labelledby', heading.id);
    return heading;
  };

  /**
   * "Tilføj" i dialogen: læg tilvalget i kurven (med rækkens antal) og gen-render dialogen.
   * @param {HTMLButtonElement} button - [data-pt-powerstep-add].
   */
  const handlePowerstepAdd = async (button) => {
    if (!psState || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const row = button.closest(ROW);
    // Ingen variant valgt endnu: åbn vælgeren i stedet (knappen er aldrig disabled af den grund).
    if (!button.dataset.ptAddonVariantId && needsVariant(row)) {
      promptVariant(row);
      return;
    }
    // Et linje-antal er ved at blive gemt; dialogen gen-renderes straks bagefter.
    if (psLineBusy) return;

    const card = button.closest(PS_ROOT);
    const select = row ? row.querySelector(SELECT) : null;
    const variantId = parseInt(button.dataset.ptAddonVariantId || (select ? select.value : ''), 10);
    if (!variantId) return;

    const qtyInput = row ? row.querySelector(QTY_INPUT) : null;
    const quantity = qtyInput ? normalizeQty(qtyInput) : psState.quantity;
    const token = psToken;
    const title = (row && row.dataset.ptAddonTitle) || '';
    const addedText = (card && card.dataset.addedText) || '';
    setRowError(row, '');
    setAddBusy(button, select, true);
    psAddBusy += 1; // linje-antal venter, til dialogen er gen-renderet

    try {
      let result = null;
      try {
        result = await postItems([{ variantId, quantity }], 'cart-icon-bubble');
      } catch (error) {
        result = null;
      }

      if (!result || !result.ok || !result.ids.has(variantId)) {
        setAddBusy(button, select, false);
        setRowError(
          row,
          errorReason(result && result.json) || (card && card.dataset.addError) || genericError()
        );
        return;
      }

      updateCartIcon(result.json);
      if (!psState.variantIds.includes(variantId)) psState.variantIds.push(variantId);
      psState.failed = psState.failed.filter((item) => Number(item.variantId) !== variantId);

      // Lukket (eller genåbnet) imens: tilvalget er i kurven; intet at gen-rendere.
      if (token !== psToken || !psDialog.open) return;

      try {
        const root = await fetchPowerstep(psState.url);
        if (token !== psToken || !psDialog.open) return;
        const heading = renderPowerstep(root);
        if (heading) heading.focus();
        if (psStatus && addedText) {
          psStatus.textContent = '';
          window.setTimeout(() => {
            psStatus.textContent = title ? `${addedText}: ${title}` : addedText;
          }, 100);
        }
      } catch (error) {
        goToCart();
      }
    } finally {
      psAddBusy -= 1;
    }
  };

  /**
   * En kurvlinje i kortet ud fra dens key.
   * @param {Element|null} card - [data-pt-powerstep].
   * @param {string} key - data-line-key.
   * @returns {Element|null}
   */
  const findLine = (card, key) => {
    if (!card || !key) return null;
    return Array.from(card.querySelectorAll(PS_LINE)).find((line) => line.dataset.lineKey === key) || null;
  };

  /**
   * Viser en fejl på en kurvlinje (erstatter en tidligere fejl).
   * @param {Element|null} line - [data-pt-line].
   * @param {string} [message] - Fejltekst; tom = fjern fejlen.
   */
  const setLineError = (line, message) => {
    if (!line) return;
    const old = line.querySelector('.pt-powerstep__line-error');
    if (old) old.remove();
    if (!message) return;
    const error = document.createElement('p');
    error.className = 'pt-powerstep__line-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    (line.querySelector('.pt-powerstep__line-info') || line).appendChild(error);
  };

  /**
   * Kurvlinjernes optaget-tilstand under /cart/change.js: antalsvælgere disabled, aria-busy.
   * @param {Element|null} card - [data-pt-powerstep].
   * @param {boolean} busy - Optaget?
   */
  const setLinesBusy = (card, busy) => {
    if (!card) return;
    card.querySelectorAll(PS_LINE).forEach((line) => {
      if (busy) line.setAttribute('aria-busy', 'true');
      else line.removeAttribute('aria-busy');
      setQtyDisabled(line, busy);
      const remove = line.querySelector(PS_LINE_REMOVE);
      if (remove) remove.disabled = busy;
    });
  };

  /**
   * Hvilken antalsvælger-kontrol på en linje der har fokus (så fokus kan genskabes efter render).
   * @param {Element|null} card - [data-pt-powerstep].
   * @returns {{key: string, step: string}|null}
   */
  const getLineFocus = (card) => {
    const active = document.activeElement;
    if (!card || !(active instanceof Element) || !card.contains(active)) return null;
    const line = active.closest(PS_LINE);
    if (!line || !line.dataset.lineKey || !active.closest(QTY)) return null;
    return { key: line.dataset.lineKey, step: active.dataset.ptQtyStep || '' };
  };

  /**
   * Giver fokus til samme kontrol på samme linje (ellers linjens antalsfelt).
   * @param {Element|null} card - [data-pt-powerstep].
   * @param {{key: string, step: string}|null} focus - Fra getLineFocus (eller en linje-key).
   * @returns {boolean} true, hvis fokus blev sat.
   */
  const restoreLineFocus = (card, focus) => {
    const line = focus ? findLine(card, focus.key) : null;
    if (!line || line.hidden) return false;
    let target = focus.step ? line.querySelector(`[data-pt-qty-step="${focus.step}"]`) : null;
    if (!target || target.disabled) target = line.querySelector(QTY_INPUT);
    if (!target || target.disabled) return false;
    target.focus();
    return true;
  };

  /**
   * Ændrer en kurvlinjes antal.
   * @param {string} key - Linjens key.
   * @param {number} quantity - Nyt antal.
   * @returns {Promise<{ok: boolean, json: object|null}>}
   */
  const postLineChange = (key, quantity) => {
    const route = (window.theme && window.theme.routes && window.theme.routes.cartChange) || '/cart/change';
    return postCart(route, {
      id: key,
      quantity,
      sections: 'cart-icon-bubble',
      sections_url: window.location.pathname
    });
  };

  /**
   * Sender de ventende linje-antal (én anmodning ad gangen) og gen-renderer dialogen.
   * Lykkes ingen, gen-renderes der ikke: værdierne går tilbage, og fejlen vises på linjen.
   * @returns {Promise<void>} Kaster aldrig.
   */
  const flushLineChanges = async () => {
    psLineTimer = 0;
    if (!psLineChanges.size) return;
    if (psLineBusy || psAddBusy) {
      psLineTimer = window.setTimeout(flushLineChanges, PS_LINE_DEBOUNCE);
      return;
    }

    const token = psToken;
    const card = psContent ? psContent.querySelector(PS_ROOT) : null;
    const changes = Array.from(psLineChanges.entries());
    psLineChanges.clear();
    const focus = getLineFocus(card) || { key: changes[0][0], step: '' };
    const fallbackError = (card && card.dataset.qtyError) || genericError();

    psLineBusy = true;
    setLinesBusy(card, true);

    const errors = new Map();
    let lastJson = null;
    try {
      for (const [key, quantity] of changes) {
        let result = null;
        try {
          result = await postLineChange(key, quantity); // eslint-disable-line no-await-in-loop
        } catch (error) {
          result = null;
        }
        if (result && result.ok) lastJson = result.json;
        else errors.set(key, errorReason(result && result.json) || fallbackError);
      }

      if (lastJson) updateCartIcon(lastJson);
      // Lukket (eller genåbnet) imens: kurven er opdateret; intet at vise.
      if (token !== psToken || !psDialog || !psDialog.open) return;

      if (lastJson) {
        const root = await fetchPowerstep(psState.url);
        if (token !== psToken || !psDialog.open) return;
        const heading = renderPowerstep(root);
        const newCard = psContent.querySelector(PS_ROOT);
        // Alle viste linjer slettet: intet at bekræfte længere.
        if (newCard && !newCard.querySelector(`${PS_LINE}:not([hidden])`)) {
          closePowerstep();
          return;
        }
        errors.forEach((message, key) => setLineError(findLine(newCard, key), message));
        if (!restoreLineFocus(newCard, focus) && heading) heading.focus();
      } else {
        // Intet blev ændret: værdierne tilbage til kurvens (value-attributten), fejl på linjerne.
        setLinesBusy(card, false);
        errors.forEach((message, key) => {
          const line = findLine(card, key);
          const input = line ? line.querySelector(QTY_INPUT) : null;
          if (input) input.value = input.defaultValue;
          setLineError(line, message);
        });
        restoreLineFocus(card, focus);
      }
    } catch (error) {
      if (token === psToken && psDialog && psDialog.open) goToCart();
    } finally {
      psLineBusy = false;
    }
  };

  /**
   * Et linje-antal er ændret: gem det (seneste vinder) og send efter en kort pause.
   * @param {Element} line - [data-pt-line].
   * @param {HTMLInputElement} input - Linjens [data-pt-qty-input].
   */
  const queueLineChange = (line, input) => {
    const key = line.dataset.lineKey;
    if (!key || !psState) return;

    const quantity = normalizeQty(input);
    setLineError(line, '');
    // Tilbage på kurvens antal (value-attributten): intet at sende.
    if (quantity === parseQuantity(input.defaultValue)) psLineChanges.delete(key);
    else psLineChanges.set(key, quantity);

    window.clearTimeout(psLineTimer);
    psLineTimer = window.setTimeout(flushLineChanges, PS_LINE_DEBOUNCE);
  };

  const handleDialogChange = (evt) => {
    const { target } = evt;
    if (!(target instanceof HTMLInputElement) || !target.matches(QTY_INPUT)) return;
    const line = target.closest(PS_LINE);
    if (line) queueLineChange(line, target);
  };

  const handleDialogClick = (evt) => {
    const { target } = evt;
    if (target === psDialog) {
      // Klik på backdrop (kun når klikket også startede dér, fx ikke ved tekstmarkering).
      if (psPointerOnBackdrop) closePowerstep();
      psPointerOnBackdrop = false;
      return;
    }
    if (!(target instanceof Element)) return;

    // PT-CUSTOM: cart-analytics — kortets link til kurven (dialogens eneste); navigationen fortsætter uændret.
    // Lytteren sidder på dialogen, som oprettes én gang, så hvert klik giver ét event, uanset hvor tit den åbnes.
    const link = target.closest('a[href]');
    if (link && isCartLink(link)) ptTrack('powerstep_to_cart', { source: 'cart_link' });

    if (target.closest(PS_CLOSE)) {
      closePowerstep();
      return;
    }

    const removeButton = target.closest(PS_LINE_REMOVE);
    if (removeButton) {
      const line = removeButton.closest(PS_LINE);
      if (!line || !line.dataset.lineKey || removeButton.disabled || !psState) return;
      setLineError(line, '');
      psLineChanges.set(line.dataset.lineKey, 0);
      window.clearTimeout(psLineTimer);
      flushLineChanges(); // slet sendes straks (ingen debounce)
      return;
    }

    const addButton = target.closest(ADD_BUTTON);
    if (addButton) handlePowerstepAdd(addButton);
  };

  const handleDialogClose = () => {
    if (psCleanedUp) return; // allerede ryddet op (knap/X lukker direkte; close-eventet kommer bagefter)
    psCleanedUp = true;
    psToken += 1;
    document.documentElement.classList.remove(PS_OPEN_CLASS);
    if (psStatus) psStatus.textContent = '';

    // Et linje-antal, der endnu ikke er sendt, gemmes straks (kurvikonet opdateres; ingen render).
    if (psLineTimer) {
      window.clearTimeout(psLineTimer);
      flushLineChanges();
    }

    const target = psReturnFocus;
    psReturnFocus = null;
    if (target && target.isConnected && typeof target.focus === 'function') {
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        // Ignorér: fokus kan ikke flyttes til elementet.
      }
    }
  };

  /**
   * Opretter dialogen første gang (appended til <body>).
   * @returns {HTMLDialogElement}
   */
  const getPowerstepDialog = () => {
    if (psDialog) return psDialog;

    const dialog = document.createElement('dialog');
    dialog.className = 'pt-powerstep';

    const content = document.createElement('div');
    content.className = 'pt-powerstep__content';

    const status = document.createElement('p');
    status.className = 'visually-hidden';
    status.setAttribute('role', 'status');

    dialog.append(content, status);
    dialog.addEventListener('pointerdown', (evt) => {
      psPointerOnBackdrop = evt.target === dialog;
    });
    dialog.addEventListener('click', handleDialogClick);
    dialog.addEventListener('change', handleDialogChange);
    dialog.addEventListener('close', handleDialogClose);
    document.body.appendChild(dialog);

    psDialog = dialog;
    psContent = content;
    psStatus = status;
    return dialog;
  };

  /**
   * Åbner powerstep'et for en netop tilføjet variant.
   * @param {number|string} variantId - Variant-id fra eventet.
   * @param {Element|null} [returnFocus] - Element, der får fokus igen ved lukning.
   * @returns {Promise<void>} Kaster aldrig; ved fejl sendes kunden til kurven.
   */
  const openPowerstep = async (variantId, returnFocus) => {
    const id = Number(variantId);
    if (!id) return;

    psToken += 1;
    const token = psToken;

    try {
      if (!dialogSupported()) throw new Error('powerstep: <dialog> not supported');

      const result = window.PT.productAddons ? window.PT.productAddons.lastResult : null;
      const fresh = !!(result && result !== psConsumed && result.mainItem
        && Number(result.mainItem.variant_id) === id);
      if (result) psConsumed = result;

      let url = fresh && typeof result.mainItem.url === 'string' ? result.mainItem.url : '';
      if (!url) url = await lineUrlFromCart(id);
      if (!url) throw new Error('powerstep: product url not found');

      // lastResult.added = [{ variantId, quantity }] (tal tolereres også).
      const added = fresh && Array.isArray(result.added)
        ? result.added.map((item) => Number(item && typeof item === 'object' ? item.variantId : item))
        : [];
      const state = {
        url,
        variantIds: [id, ...added.filter((addedId) => addedId && addedId !== id)],
        failed: fresh && Array.isArray(result.failed) ? result.failed.slice() : [],
        quantity: fresh ? parseQuantity(result.quantity) : 1
      };

      const root = await fetchPowerstep(url);
      if (token !== psToken) return;

      const dialog = getPowerstepDialog();
      psState = state;
      const heading = renderPowerstep(root);

      if (!dialog.open) {
        psReturnFocus = returnFocus || null;
        dialog.showModal();
        psCleanedUp = false;
        document.documentElement.classList.add(PS_OPEN_CLASS);
      }
      if (heading) heading.focus();
    } catch (error) {
      if (token === psToken) goToCart();
    }
  };

  const handleCartEvent = (evt) => {
    try {
      if (!powerstepEnabled()) return;

      const { target } = evt;
      if (!(target instanceof Element) || !target.closest('product-form')) return;
      if (target.closest('quick-add-drawer')) return;
      if (window.location.pathname === cartUrl()) return;

      const variantId = evt.detail ? Number(evt.detail.variantId) : 0;
      if (!variantId) return;

      openPowerstep(variantId, document.activeElement);
    } catch (error) {
      // Må aldrig forstyrre temaets egen håndtering af eventet.
    }
  };

  document.addEventListener('on:cart:add', handleCartEvent);
  document.addEventListener('on:line-item:change', handleCartEvent);

  // Til test i konsollen: PT.powerstep.open(<variant-id>) / PT.powerstep.close().
  window.PT.powerstep = {
    open: (variantId) => openPowerstep(variantId, document.activeElement),
    close: closePowerstep
  };
  /* PT-CUSTOM: powerstep — slut */

  /* PT-CUSTOM: contact-analytics — GA4-events for kontakt (Google Shopping-plan 1.9). Usynligt tillæg; sender kun via
   * ptTrack (window.PT.track) og arver dens regler: whitelist i PT_EVENTS, Shopify.analytics.publish('pt:<navn>') og
   * GA4 kun via appens gtag med analytics-samtykke og send_to G-5B8C4PJHTM (holder Ads-målene ude).
   * - contact_click {method tel|mail, source}: klik på tel:/mailto:-links på alle sider (ét delegeret capture-bind på
   *   document). source, første match vinder: note (.pt-note/<pt-notes>), selector (maskinvælgeren .pt-sel/
   *   <pt-selector>), cart (#cart eller sektionen med #cart, som getContactSource i pt-cart.js), pdp (.pt-pdp-trust
   *   eller produktsidens hovedsektion .cc-main-product), header, footer, page (<main>), ellers other. Hverken nummer
   *   eller adresse sendes, og klikket fortsætter uændret.
   * - contact_form_sent {source contact|selector|signup}: formularer med form_type=contact (footerens nyhedsbrev har
   *   form_type=customer og ignoreres). Ved submit (capture; også når et script stopper submit'en, fx Shopifys
   *   captcha, der sender igen med form.submit() uden nyt submit-event) gemmes {source, path, t} i sessionStorage
   *   pt-contact-pending, aldrig feltværdier. Eventet
   *   sendes først, når siden indlæses med ?contact_posted=true på samme sti inden for 30 min; nøglen slettes før
   *   afsendelsen, så en genindlæsning ikke tæller igen. Appens gtag defineres sent: afsendelsen venter på load og
   *   prøver én gang mere efter 2 s, hvis gtag mangler (én afsendelse i alt).
   * - Dobbelttælling: på /cart sender et klik både K2's cart_contact_click (pt-cart.js, uændret) og contact_click;
   *   kun contact_click er nøglehændelse, og de to må ikke lægges sammen.
   * - Kendte huller: blokeret storage → intet contact_form_sent; en server-afvisning, der alligevel redirecter med
   *   contact_posted=true, tæller; gtag senere end load + 2 s → kun Shopify-publish.
   * Kaster aldrig. Rene hjælpere på window.PT.contact (enhedstest: tests/contact.test.mjs). */
  const CONTACT_PENDING_KEY = 'pt-contact-pending';
  const CONTACT_PENDING_MAX_MS = 30 * 60 * 1000;
  const CONTACT_FORM_SOURCES = ['contact', 'selector', 'signup'];
  const CONTACT_GA_RETRY_MS = 2000;

  /**
   * Kontaktmetoden for et links href.
   * @param {string|null} href - Linkets href-attribut.
   * @returns {string} 'tel', 'mail' eller '' (ikke et kontaktlink).
   */
  const contactMethod = (href) => {
    const value = typeof href === 'string' ? href.trim().toLowerCase() : '';
    if (value.startsWith('tel:')) return 'tel';
    if (value.startsWith('mailto:')) return 'mail';
    return '';
  };

  /**
   * Om siden er Shopifys svar på en indsendt kontaktformular (?contact_posted=true).
   * @param {string} search - location.search.
   * @returns {boolean}
   */
  const isContactPosted = (search) => {
    try {
      return new URLSearchParams(typeof search === 'string' ? search : '').get('contact_posted') === 'true';
    } catch (error) {
      return false;
    }
  };

  /**
   * Kilden fra en ventende indsendelse, hvis den passer til siden og ikke er for gammel.
   * @param {string|null} raw - sessionStorage-værdien (JSON {source, path, t}).
   * @param {string} path - location.pathname.
   * @param {number} now - Tidspunkt i ms (Date.now()).
   * @returns {string} 'contact', 'selector', 'signup' eller '' (ingen gyldig indsendelse).
   */
  const readContactPending = (raw, path, now) => {
    try {
      if (typeof raw !== 'string' || !raw) return '';
      const pending = JSON.parse(raw);
      if (!pending || typeof pending !== 'object') return '';
      const { source, t } = pending;
      if (typeof source !== 'string' || !CONTACT_FORM_SOURCES.includes(source)) return '';
      if (typeof pending.path !== 'string' || pending.path !== path) return '';
      if (typeof t !== 'number' || !Number.isFinite(t) || typeof now !== 'number') return '';
      const age = now - t;
      return age >= 0 && age <= CONTACT_PENDING_MAX_MS ? source : '';
    } catch (error) {
      return '';
    }
  };

  /**
   * Hvor på siden et kontaktlink står (første match vinder).
   * @param {Element} link - Linket.
   * @returns {string} En af kilderne i PT_EVENTS.contact_click.source.
   */
  const getContactClickSource = (link) => {
    if (link.closest('.pt-note, pt-notes')) return 'note';
    if (link.closest('.pt-sel, pt-selector')) return 'selector';
    const section = link.closest('.shopify-section');
    if (link.closest('#cart') || (section && section.querySelector('#cart'))) return 'cart';
    // .cc-main-product = main-product's sektions-wrapper (schema-klassen); vendorens egne scripts bruger den også.
    if (link.closest('.pt-pdp-trust, .cc-main-product')) return 'pdp';
    if (link.closest('.shopify-section-group-header-group, header')) return 'header';
    if (link.closest('.shopify-section-group-footer-group, footer')) return 'footer';
    if (link.closest('main')) return 'page';
    return 'other';
  };

  /**
   * Handles 'click' på document (capture, delegeret): tel:/mailto:-links → contact_click {method, source}.
   * @param {MouseEvent} evt - Event object.
   */
  const handleContactLinkClick = (evt) => {
    try {
      if (typeof Element !== 'function' || !(evt.target instanceof Element)) return;
      const link = evt.target.closest('a[href]');
      if (!link) return;
      const method = contactMethod(link.getAttribute('href'));
      if (!method) return;
      ptTrack('contact_click', { method, source: getContactClickSource(link) });
    } catch (error) {
      // Hændelser må aldrig forstyrre klikket.
    }
  };

  /**
   * Handles 'submit' på document (capture): en kontaktformular, der sendes, gemmes som ventende indsendelse.
   * defaultPrevented tjekkes ikke: kun ?contact_posted=true på samme sti tæller, så et stoppet forsøg giver intet event.
   * @param {SubmitEvent} evt - Event object.
   */
  const handleContactSubmit = (evt) => {
    try {
      const form = evt.target;
      if (typeof HTMLFormElement !== 'function' || !(form instanceof HTMLFormElement)) return;
      const formType = form.querySelector('input[name="form_type"]');
      if (!formType || formType.value !== 'contact') return;

      let source = 'contact';
      if (form.closest('.pt-sel, pt-selector') || form.classList.contains('pt-sel__lead')) source = 'selector';
      else if (form.getAttribute('id') === 'product-signup') source = 'signup';

      window.sessionStorage.setItem(CONTACT_PENDING_KEY, JSON.stringify({
        source,
        path: window.location.pathname,
        t: Date.now()
      }));
    } catch (error) {
      // Blokeret storage: ingen bekræftelse (kendt hul). Indsendelsen fortsætter uændret.
    }
  };

  /**
   * Ved sideindlæsning: ?contact_posted=true + en gyldig ventende indsendelse → contact_form_sent (én gang).
   */
  const confirmContactSent = () => {
    try {
      if (!isContactPosted(window.location.search)) return;
      let source = '';
      try {
        const storage = window.sessionStorage;
        source = readContactPending(storage.getItem(CONTACT_PENDING_KEY), window.location.pathname, Date.now());
        if (!source) return;
        storage.removeItem(CONTACT_PENDING_KEY); // først, så en genindlæsning ikke tæller igen
      } catch (error) {
        return; // Storage kaster → intet event (kendt hul).
      }

      const send = () => ptTrack('contact_form_sent', { source });
      const run = () => {
        try {
          if (typeof window.gtag === 'function') send();
          else window.setTimeout(send, CONTACT_GA_RETRY_MS);
        } catch (error) {
          // Hændelser må aldrig forstyrre siden.
        }
      };
      if (document.readyState === 'complete') run();
      else window.addEventListener('load', run, { once: true });
    } catch (error) {
      // Hændelser må aldrig forstyrre siden.
    }
  };

  document.addEventListener('click', handleContactLinkClick, true);
  document.addEventListener('submit', handleContactSubmit, true);
  confirmContactSent();

  window.PT.contact = {
    method: contactMethod,
    isPosted: isContactPosted,
    readPending: readContactPending
  };
  /* PT-CUSTOM: contact-analytics — slut */

  /* PT-CUSTOM: header-nav — ☰-panelet ved 769–1179px lukker ved Escape, klik udenfor og bredere vindue.
   * CSS'en (pt-custom.css, header-nav d) viser vendorens ☰-knap ved 769–1179px og lægger menuen i et panel under
   * headeren. Vendorens MainMenu (assets/main.js L1347–1416) åbner og lukker panelet uden at se på breakpoint, men fra
   * 769px lukker dens handleClose (Escape i menuen og klik på overlayet, L1584) kun en åben dropdown, ikke panelet.
   * - Escape og klik udenfor lukker gennem vendorens egen knap (toggle.click() → handleMainMenuToggle →
   *   closeMainMenu), så aria-expanded, scroll-låsen (body.overflow-hidden) og fokusfælden følger med, og fokus går
   *   tilbage til ☰.
   * - Escape: står en dropdown åben, lukker vendoren den først (dens keyup-lytter på ul.main-nav, når fokus er i
   *   menuen). Dropdownen beholder is-open, til dens transition er færdig, så samme tastetryk lukker ikke panelet;
   *   næste Escape gør.
   * - Klik uden for store-header lukker panelet. Klik i headeren (også på ☰) håndteres af vendoren.
   * - Bredere vindue: 1180px er ikke et af vendorens breakpoints (769/1024/1280/1536 og orientering nulstiller menuen
   *   med init → closeMainMenu(false), main.js L1220), og fra 1180px er knappen skjult. Uden change-lukningen ville
   *   body.overflow-hidden blive hængende, når et desktopvindue med åbent panel trækkes bredere end 1179px. Her gør
   *   blokken som vendoren ved sine egne breakpoints og kalder closeMainMenu(false) på <main-menu>: et klik på knappen
   *   er ikke nok, for d)'s opacity-transition gælder ikke fra 1180px, så transitionend kommer aldrig, og is-open og
   *   fokusfælden blev hængende (målt ved 1100 → 1250px: Tab kørte i ring i menuen). Mangler metoden efter en
   *   temaopdatering, bruges knappen som de to andre lukninger.
   * - --header-height: vendoren sætter den kun i StoreHeader.setMenuHeight (main.js L1082–1087), når menuen har en
   *   højde (window.setHeaderHeight, L169, leder efter #shopify-section-header, som ikke findes med section groups).
   *   Ved 769–1179px er menuen skjult, så variablen manglede, og produktsidens sticky kolonne
   *   (assets/sticky-scroll-direction.js, læser den ved init) fik top = NaN og mistede scroll-retningen. Blokken
   *   sætter den med vendorens mål (store-header's clientHeight) ved indlæsning, ved resize og ved skift ind i
   *   769–1179px. pt-custom.js loades med defer før main.js og sticky-scroll-direction.js, så værdien står klar, når
   *   kolonnen læser den; fra 1180px måler vendoren selv.
   * Lytterne (Escape, klik og change) kører først ved brugerhandlinger, så det er uden betydning, at main.js loades
   * efter pt-custom.js. Gør intet, hvis knappen, panelet eller headeren mangler. */
  const NAV_BURGER_MQ = window.matchMedia('(min-width: 769px) and (max-width: 1179.98px)');

  const closeNavPanel = () => {
    const toggle = document.querySelector('main-menu .main-menu__toggle');
    if (toggle && document.querySelector('main-menu .main-menu__content.is-open.is-visible')) toggle.click();
  };

  document.addEventListener('keyup', (evt) => {
    if (NAV_BURGER_MQ.matches && evt.key === 'Escape'
      && !document.querySelector('.main-nav__item--dropdown.is-open')) closeNavPanel();
  });

  document.addEventListener('click', (evt) => {
    if (NAV_BURGER_MQ.matches && evt.target instanceof Element && !evt.target.closest('store-header')) {
      closeNavPanel();
    }
  });

  const setNavHeaderHeight = () => {
    const header = document.querySelector('store-header');
    if (NAV_BURGER_MQ.matches && header && header.clientHeight) {
      document.documentElement.style.setProperty('--header-height', `${header.clientHeight}px`);
    }
  };
  setNavHeaderHeight();
  window.addEventListener('on:debounced-resize', setNavHeaderHeight);

  const handleNavBurgerChange = () => {
    if (NAV_BURGER_MQ.matches) {
      setNavHeaderHeight();
      return;
    }
    const menu = document.querySelector('main-menu');
    if (!menu || !menu.querySelector('.main-menu__content.is-open')) return;
    if (typeof menu.closeMainMenu === 'function') {
      menu.closeMainMenu(false);
    } else {
      closeNavPanel();
    }
  };
  if (typeof NAV_BURGER_MQ.addEventListener === 'function') {
    NAV_BURGER_MQ.addEventListener('change', handleNavBurgerChange);
  } else if (typeof NAV_BURGER_MQ.addListener === 'function') {
    NAV_BURGER_MQ.addListener(handleNavBurgerChange); // Safari < 14
  }

  // Scroll-lås og fokusfælde efter en dropdown i det åbne panel: fra 769px fjerner vendorens close() scroll-låsen
  // (body.overflow-hidden, main.js L1755–1757), og handleTransition (L1568) fjerner fokusfælden, når dropdownen er
  // lukket, selv om ☰-panelet stadig er åbent. Siden kunne så scrolles bag panelet, og Tab kunne forlade menuen.
  // Når den sidste dropdown er lukket (transitionend på .main-nav__child, efter vendorens egen lytter på ul.main-nav),
  // sættes låsen og fælden på igen, som openMainMenu gør (main.js L1377–1380). Fokus bliver, hvor det er. Skifter man
  // mellem to dropdowns, er den nye åben, og vendoren har selv låst og fanget fokus i den.
  document.addEventListener('transitionend', (evt) => {
    if (!NAV_BURGER_MQ.matches || !(evt.target instanceof Element) || !evt.target.matches('.main-nav__child')) return;
    const menu = document.querySelector('main-menu');
    if (!menu || !menu.querySelector('.main-menu__content.is-open.is-visible')) return;
    if (menu.querySelector('.main-nav__item--dropdown.is-open')) return;
    document.body.classList.add('overflow-hidden');
    if (typeof window.trapFocus === 'function') window.trapFocus(menu, document.activeElement);
  });
  /* PT-CUSTOM: header-nav — slut */

  /* PT-CUSTOM: maskinvaelger-open — maskinvælgeren fra indgangene (D4): dialog på desktop, siden på mobil.
   * Indgangene er almindelige links til vælgersiden med data-pt-selector-open (+ data-pt-selector-source):
   * snippets/pt-selector-link.liquid (produktsiden), snippets/pt-selector-card.liquid (kollektionsgitteret); pt-notes
   * kalder window.PT.selector.open({ source: 'note' }) direkte.
   * - window.PT.selector.open({ url, source, preset, fra, opener }) → Promise. url = vælgersiden (standard
   *   /pages/maskinvaelger); fra/preset/kilde læses fra url'en (opts er reserve). Under 769px eller uden <dialog>:
   *   location.assign(url) med kilde (= source) tilføjet, hvis den mangler – siden tager over (side-tilstand, trin-
   *   historik, måling). Fra 769px: <sti>?section_id=pt-selector hentes (cache: 'no-store', timeout 6 s), parses med
   *   DOMParser, sektionens stylesheet loades (venter på load) og dens scripts i rækkefølge (product-form.js,
   *   pt-selector-engine.js, pt-selector.js; scripts i indsat HTML kører ikke). Asset-URL'erne læses fra svaret (ingen
   *   hardkodede ?v=); dubletter findes på pathname (fx product-form.js på produktsiden), kun samme host eller Shopifys
   *   CDN. Derefter window.PT.selectorDialog.open({ node, path, preset, fra, source, opener }) (assets/pt-selector.js).
   *   Er vælgeren allerede indlæst i dialogen, genbruges den med svarene. Linket har aria-busy under hentningen.
   *   Enhver fejl → location.assign(url); på vælgersiden selv navigeres der også (ingen dialog oven på siden).
   *   Dialogen rører aldrig historik/URL (assets/facet-filters.js genanvender
   *   filtre ved popstate).
   * - Hændelsen selector_open { source, mode: 'dialog' } sendes, når dialogen er åben (siden sender selv
   *   mode: 'page' ved load ud fra kilde, og T1: source 'direct' ved direkte besøg). Gyldige ?kilde= = PT_SEL_SOURCES
   *   (uden 'direct', så et link med ?kilde=direct aldrig giver en indgang).
   * - Delegering på document: klik på a[data-pt-selector-open] (kun venstreklik uden modifier, kun ≥ 769px med
   *   <dialog>) → preventDefault + open(); ellers følges linket. pointerover/focusin på linket → prefetch(href) én gang
   *   pr. link (hentningen deles pr. sti; en fejlet hentning prøves igen ved næste åbning).
   * - window.PT.selectorLoaderUtils: rene URL-hjælpere (testet i vm: tests/selector-entry.test.mjs).
   * Rører intet ved load ud over to lyttere på document, så filen kan loades i vm. */
  const SEL_PAGE = '/pages/maskinvaelger';
  const SEL_MQ = '(min-width: 769px)';
  const SEL_TIMEOUT = 6000;
  const SEL_SOURCES = PT_SEL_SOURCES; // T1: ikke PT_EVENTS.selector_open.source ('direct' er ingen indgang)
  const SEL_LINK = 'a[data-pt-selector-open]';
  const selFetches = new Map(); // sti → Promise<string> (sektionens HTML)
  const selPrefetchedLinks = new WeakSet();
  let selBusy = null;

  const selUtils = {
    /**
     * Vælgerlinkets dele.
     * @param {string} url - Link (relativt eller absolut); tomt = /pages/maskinvaelger.
     * @param {string} base - Basis-URL (window.location.href).
     * @returns {{ href: string, origin: string, path: string, fra: string, preset: string, kilde: string }|null}
     */
    parse(url, base) {
      try {
        const u = new URL(url || SEL_PAGE, base);
        if (!/^https?:$/.test(u.protocol)) return null;
        return {
          href: u.href,
          origin: u.origin,
          path: u.pathname,
          fra: u.searchParams.get('fra') || '',
          preset: u.searchParams.get('preset') || '',
          kilde: u.searchParams.get('kilde') || ''
        };
      } catch (error) {
        return null;
      }
    },
    /** Gyldig kilde eller reserven. */
    source(value, fallback) {
      return SEL_SOURCES.includes(value) ? value : fallback;
    },
    /** Linket med kilde=<source>, hvis kilde mangler (siden måler indgangen ud fra den). */
    withSource(url, source, base) {
      try {
        const u = new URL(url || SEL_PAGE, base);
        if (SEL_SOURCES.includes(source) && !u.searchParams.has('kilde')) u.searchParams.set('kilde', source);
        return u.href;
      } catch (error) {
        return url || SEL_PAGE;
      }
    },
    /** Section Rendering-URL'en til sektionen pt-selector i sidens kontekst. */
    sectionUrl(path) {
      return `${path || SEL_PAGE}?section_id=pt-selector`;
    },
    /**
     * Pathname for en asset-URL fra svaret – kun samme host eller Shopifys CDN; ellers ''.
     * @param {string} url - src/href fra svaret.
     * @param {string} base - Basis-URL (window.location.href).
     */
    assetPath(url, base) {
      try {
        const u = new URL(url, base);
        const host = new URL(base).host;
        if (!/^https?:$/.test(u.protocol)) return '';
        return u.host === host || /(^|\.)cdn\.shopify\.com$/.test(u.hostname) ? u.pathname : '';
      } catch (error) {
        return '';
      }
    }
  };

  const selDesktop = () => !!(window.matchMedia && window.matchMedia(SEL_MQ).matches);

  /** Henter sektionens HTML én gang pr. sti (deles af prefetch og open). Afviser efter SEL_TIMEOUT. */
  const selFetch = (path) => {
    if (selFetches.has(path)) return selFetches.get(path);
    const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
    let timer = 0;
    const timeout = new Promise((resolve, reject) => {
      timer = window.setTimeout(() => {
        if (controller) controller.abort();
        reject(new Error('maskinvælger: timeout'));
      }, SEL_TIMEOUT);
    });
    const request = fetch(selUtils.sectionUrl(path), {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    }).then((res) => {
      if (!res.ok) throw new Error(`maskinvælger: HTTP ${res.status}`);
      return res.text();
    });
    const promise = Promise.race([request, timeout]).finally(() => window.clearTimeout(timer));
    selFetches.set(path, promise);
    promise.catch(() => selFetches.delete(path)); // næste forsøg henter igen
    return promise;
  };

  /** Loader ét asset (stylesheet eller script) og venter på load. */
  const selLoad = (tag, url) => new Promise((resolve, reject) => {
    const el = document.createElement(tag);
    el.addEventListener('load', () => resolve(), { once: true });
    el.addEventListener('error', () => reject(new Error(`maskinvælger: ${url} kunne ikke indlæses`)), { once: true });
    if (tag === 'link') {
      el.rel = 'stylesheet';
      el.href = url;
      document.head.appendChild(el);
    } else {
      el.src = url;
      el.async = false;
      document.head.appendChild(el);
    }
  });

  /** Sektionens stylesheets og scripts fra svaret, i rækkefølge og uden dubletter (pathname). */
  const selLoadAssets = async (doc) => {
    const base = window.location.href;
    const loaded = (selector, attr) => Array.from(document.querySelectorAll(selector))
      .map((el) => selUtils.assetPath(el.getAttribute(attr), base));
    const sheets = loaded('link[rel="stylesheet"][href]', 'href');
    const scripts = loaded('script[src]', 'src');
    const css = [];
    doc.querySelectorAll('link[rel="stylesheet"][href]').forEach((el) => {
      const href = el.getAttribute('href');
      const path = selUtils.assetPath(href, base);
      if (!path) throw new Error('maskinvælger: fremmed stylesheet');
      if (!sheets.includes(path)) css.push(selLoad('link', new URL(href, base).href));
    });
    await Promise.all(css);
    const srcs = Array.from(doc.querySelectorAll('script[src]')).map((el) => el.getAttribute('src'));
    for (const src of srcs) {
      const path = selUtils.assetPath(src, base);
      if (!path) throw new Error('maskinvælger: fremmed script');
      if (!scripts.includes(path)) {
        await selLoad('script', new URL(src, base).href); // eslint-disable-line no-await-in-loop
        scripts.push(path);
      }
    }
  };

  /**
   * Åbner maskinvælgeren: dialog på desktop, ellers siden.
   * @param {object} [opts] - { url, source, preset, fra, opener }.
   * @returns {Promise<boolean>} true = dialogen er åben; false = navigerer til siden. Kaster aldrig.
   */
  const openSelector = (opts) => {
    const o = opts || {};
    const base = window.location.href;
    const target = selUtils.parse(o.url, base);
    if (!target) return Promise.resolve(false);
    const source = selUtils.source(o.source, selUtils.source(target.kilde, 'link'));
    const pageUrl = selUtils.withSource(target.href, source, base);
    const go = () => {
      window.location.assign(pageUrl);
      return false;
    };
    const trimPath = (p) => String(p || '').replace(/\/+$/, '') || '/';
    const onPage = trimPath(target.path) === trimPath(window.location.pathname); // vælgersiden selv: ingen dialog ovenpå
    if (!selDesktop() || !dialogSupported() || target.origin !== window.location.origin || onPage) return Promise.resolve(go());
    if (selBusy) return selBusy; // dobbeltklik under hentningen
    const active = document.activeElement;
    const opener = o.opener instanceof Element ? o.opener : (active instanceof Element ? active : null);
    const params = {
      path: target.path,
      preset: target.preset || (typeof o.preset === 'string' ? o.preset : ''),
      fra: target.fra || (typeof o.fra === 'string' ? o.fra : ''),
      source,
      opener
    };
    const show = (node) => {
      window.PT.selectorDialog.open({ ...params, node });
      ptTrack('selector_open', { source, mode: 'dialog' });
      return true;
    };
    selBusy = (async () => {
      const dialog = window.PT.selectorDialog;
      if (dialog && typeof dialog.loaded === 'function' && dialog.loaded(target.path)) return show(null);
      if (opener && opener.matches(SEL_LINK)) opener.setAttribute('aria-busy', 'true');
      const html = await selFetch(target.path);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('pt-selector');
      if (!node) throw new Error('maskinvælger: ingen <pt-selector> i svaret');
      await selLoadAssets(doc);
      if (!window.PT.selectorDialog || typeof window.PT.selectorDialog.open !== 'function') {
        throw new Error('maskinvælger: dialogen mangler');
      }
      return show(node);
    })()
      .catch(() => go())
      .finally(() => {
        selBusy = null;
        if (opener) opener.removeAttribute('aria-busy');
      });
    return selBusy;
  };

  /**
   * Henter vælgerens section-HTML i forvejen (desktop), så dialogen åbner hurtigt.
   * @param {string} [url] - Vælgerlinket.
   */
  const prefetchSelector = (url) => {
    try {
      if (!selDesktop() || !dialogSupported()) return;
      const target = selUtils.parse(url, window.location.href);
      if (!target || target.origin !== window.location.origin) return;
      const dialog = window.PT.selectorDialog;
      if (dialog && typeof dialog.loaded === 'function' && dialog.loaded(target.path)) return;
      selFetch(target.path).catch(() => {});
    } catch (error) {
      // Prefetch er kun en optimering.
    }
  };

  const selLinkFrom = (evt) => (evt.target instanceof Element ? evt.target.closest(SEL_LINK) : null);

  document.addEventListener('click', (evt) => {
    if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
    const link = selLinkFrom(evt);
    if (!link || !link.href || !selDesktop() || !dialogSupported()) return; // mobil: almindelig navigation
    evt.preventDefault();
    openSelector({ url: link.href, source: link.getAttribute('data-pt-selector-source'), opener: link });
  });

  const handleSelectorIntent = (evt) => {
    const link = selLinkFrom(evt);
    if (!link || selPrefetchedLinks.has(link)) return;
    selPrefetchedLinks.add(link);
    prefetchSelector(link.href);
  };
  document.addEventListener('pointerover', handleSelectorIntent);
  document.addEventListener('focusin', handleSelectorIntent);

  window.PT.selector = { open: openSelector, prefetch: prefetchSelector };
  window.PT.selectorLoaderUtils = selUtils;
  /* PT-CUSTOM: maskinvaelger-open — slut */
}());
